"use client";

import { useEffect, useState } from "react";
import { CalendarCheck, MagnifyingGlass, Phone, WhatsappLogo } from "@phosphor-icons/react/dist/ssr";
import { useTranslations } from "next-intl";
import { cn, telHrefMa } from "@/lib/utils";
import { buildWhatsappLink } from "@/lib/utils";
import { Link } from "@/lib/i18n/navigation";
import { UnclaimedCallButton } from "@/components/physician/UnclaimedCallButton";
import { canBookOnline, isOnlineBookingActive } from "@/lib/data/physician-tier";
import type { PhysicianTier } from "@/lib/data/physician-tier";
import { track } from "@/lib/observability/analytics";

type Props = {
  physicianName: string;
  /** Slug du physician — propagé au funnel tracking. */
  physicianSlug?: string;
  phone?: string;
  whatsapp?: string;
  bookingAnchor?: string;
  showAfter?: number;
  /** Tier du médecin — gate de base (≠ unclaimed). Cf. canBookOnline. */
  tier?: "gold" | "premium" | "free" | "unclaimed";
  /** RDV en ligne activé par le médecin (toggle espace pro). Requis EN PLUS du
   *  tier pour afficher le booking direct (≠ "demander RDV"). Cf. isOnlineBookingActive. */
  onlineBookingEnabled?: boolean;
  /** Tier de placement offert — analytics. */
  trialBoosted?: "gold" | "premium";
  /** Ville (slug) — segmentation analytics + URL fallback "similaires". */
  citySlug?: string;
  /** Spécialité canonique (slug) — URL fallback "similaires". */
  specialtySlug?: string;
  /**
   * Fiche cliente injoignable (acceptsAppointmentRequest) : /rdv/{slug} y
   * présente le formulaire de demande. Faux ou absent : /rdv/{slug} ne propose
   * que des praticiens similaires joignables, le bouton le dit.
   */
  acceptsRequest?: boolean;
};

const UTM_SOURCE = "physician_page_mobile_sticky";

type StickyCallTarget = {
  physicianSlug?: string;
  tier: PhysicianTier;
  trialBoosted?: "gold" | "premium";
  city?: string;
};

type StickyCallTrackEvent = {
  type: "PHONE_CLICK";
  entityType: "Physician";
  entitySlug?: string;
  source: string;
  metadata: { tier: PhysicianTier; trialBoosted: "gold" | "premium" | null; city: string | null };
};

/**
 * Les deux PHONE_CLICK d'un appel par lien tel: direct de la barre, dans
 * l'ordre d'envoi : l'intention (call-cabinet:…), que comptent les Stats Pro,
 * puis le clic sur le numéro (call-completed:…), qui la confirme sans la
 * recompter (lib/analytics/human-clicks.ts, isCallIntent). Même source, même
 * entitySlug et mêmes metadata qu'UnclaimedCallButton, que cette barre rend
 * avec source = UTM_SOURCE.
 *
 * Bug 2026-10-06 : seule la confirmation « call-completed:sticky-direct »
 * partait, ces appels n'apparaissaient jamais dans les Stats Pro du médecin.
 */
export function stickyCallTrackEvents(target: StickyCallTarget): {
  intent: StickyCallTrackEvent;
  completed: StickyCallTrackEvent;
} {
  const base: Omit<StickyCallTrackEvent, "source"> = {
    type: "PHONE_CLICK",
    entityType: "Physician",
    entitySlug: target.physicianSlug,
    metadata: { tier: target.tier, trialBoosted: target.trialBoosted ?? null, city: target.city ?? null },
  };
  return {
    intent: { ...base, source: `call-cabinet:${UTM_SOURCE}` },
    completed: { ...base, source: `call-completed:${UTM_SOURCE}` },
  };
}

/** Comme UnclaimedCallButton : sendBeacon survit au départ vers le composeur, fetch keepalive sinon. */
function sendCallTrackEvent(event: StickyCallTrackEvent): void {
  const body = JSON.stringify(event);
  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      if (navigator.sendBeacon("/api/track/event", new Blob([body], { type: "application/json" }))) return;
    }
    fetch("/api/track/event", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
      credentials: "same-origin",
    }).catch(() => {});
  } catch {
    // navigation tel: doit toujours passer
  }
}

/**
 * Clic sur un lien tel: direct de la barre : intention puis appel, en base et
 * dans Plausible/GA4, dans l'ordre du mode direct d'UnclaimedCallButton.
 */
export function trackStickyDirectCall(target: StickyCallTarget): void {
  const { intent, completed } = stickyCallTrackEvents(target);
  sendCallTrackEvent(intent);
  track("physician:call_intent", { source: UTM_SOURCE, physician_slug: target.physicianSlug });
  track("physician:contact_phone", {
    source: UTM_SOURCE,
    physician_slug: target.physicianSlug,
    tier: target.tier,
  });
  sendCallTrackEvent(completed);
}

export type StickyCtaMode = "book" | "call" | "request_rdv" | "reachable" | "similar" | "none";

/** CTA primaire de la barre (cascade décrite sur StickyBookingCTA ; l'ordre compte). */
export function stickyCtaMode(input: {
  tier?: PhysicianTier;
  onlineBookingEnabled?: boolean;
  phone?: string;
  physicianSlug?: string;
  specialtySlug?: string;
  acceptsRequest?: boolean;
}): StickyCtaMode {
  const tier = input.tier ?? "unclaimed";
  if (isOnlineBookingActive({ tier, onlineBookingEnabled: input.onlineBookingEnabled })) return "book";
  if (input.phone) return "call";
  if (canBookOnline(tier) && input.physicianSlug) return input.acceptsRequest ? "request_rdv" : "reachable";
  return input.specialtySlug ? "similar" : "none";
}

/**
 * CTA flottant mobile sur les fiches médecin — fallback intelligent en cascade :
 *
 *   1. Médecin bookable en ligne (tier ≥ free + onlineBookingEnabled)
 *      → CTA primaire = "Prendre RDV" (deeplink #booking) + tracking `booking:started`.
 *   2. Pas de booking en ligne MAIS téléphone présent → CTA primaire = "Appeler".
 *      - tier `unclaimed` → modal `UnclaimedCallButton` (numéro vérifié).
 *      - tier `free` sans toggle → `tel:` direct + tracking `physician:contact_phone`.
 *   3. Pas de téléphone, fiche cliente (acceptsRequest) → CTA primaire =
 *      "Demander un RDV" (link `/rdv/{slug}` : formulaire de demande transmise
 *      au cabinet) + RDV_INTENT.
 *   3 bis. Pas de téléphone, tier ≠ unclaimed mais fiche NON cliente (VERIFIED
 *      sans compte) → "Médecins similaires" (link `/rdv/{slug}`, qui liste des
 *      praticiens joignables), sans RDV_INTENT. Bilan du 21/10/2026 (A12) : 19
 *      des 20 clics « Demander un RDV » depuis le 06/10 visaient ces fiches, qui
 *      ne reçoivent aucune demande — le libellé promettait ce que la page ne
 *      fait pas.
 *   4. Unclaimed sans téléphone → fallback "Médecins similaires"
 *      (link `/{spe}/{ville}` ou `/recherche`).
 *
 * Toujours rendu après scroll `showAfter` px pour ne pas masquer le hero.
 * Tracking : tous les CTA primaires emit `utm_source=physician_page_mobile_sticky`
 * via le param `source` (UnclaimedCallButton) ou via les props `track()`.
 */
export function StickyBookingCTA({
  physicianName,
  physicianSlug,
  phone,
  whatsapp,
  bookingAnchor = "#booking",
  showAfter = 400,
  tier,
  onlineBookingEnabled,
  trialBoosted,
  citySlug,
  specialtySlug,
  acceptsRequest = false,
}: Props) {
  const [visible, setVisible] = useState(false);
  const t = useTranslations("physician.stickyBookingCta");

  useEffect(() => {
    function onScroll() {
      setVisible(window.scrollY > showAfter);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [showAfter]);

  const safeTier = tier ?? "unclaimed";
  const claimable = canBookOnline(safeTier); // tout sauf unclaimed
  // WhatsApp seulement sur une fiche revendiquée (06/10/2026). Tant que
  // buildWhatsappLink produisait wa.me/0661… (lien cassé), ce bouton ne
  // joignait personne ; une fois le lien réparé, il devenait actif sur ~1 000
  // fiches non revendiquées du catalogue, vers des numéros importés souvent
  // partagés (0661199167 : 52 fiches, 18 villes) — le message pré-rempli
  // partait chez un inconnu. Même raison que la page /rdv depuis le 05/10
  // (accountWhatsapp), qui exige en plus un compte rattaché : ce composant ne
  // reçoit que le tier. Numéro inutilisable → buildWhatsappLink renvoie
  // null → pas de bouton.
  const waLink = whatsapp && claimable
    ? buildWhatsappLink(whatsapp, t("whatsappMessage", { name: physicianName }))
    : null;

  // Détermine quelle CTA primaire afficher (cascade — Order matters).
  const mode = stickyCtaMode({ tier: safeTier, onlineBookingEnabled, phone, physicianSlug, specialtySlug, acceptsRequest });

  if (mode === "none") {
    // Fiche sans phone, unclaimed, sans specialty connue — rien à offrir.
    // (Cas marginal — moins de 1% du catalogue scrapé.)
    return null;
  }

  // URL fallback "similaires" — best-effort sur la spe canonique + ville,
  // sinon /recherche (jamais d'URL vide).
  const similarHref = specialtySlug
    ? citySlug
      ? `/${specialtySlug}/${citySlug}`
      : `/${specialtySlug}`
    : "/recherche";

  // ── handlers tracking ─────────────────────────────────────────────────────
  const trackBookingStarted = () => {
    // GA4/Plausible side
    track("booking:started", {
      physicianSlug,
      source: UTM_SOURCE,
      tier: safeTier,
      trialBoosted: trialBoosted ?? null,
      city: citySlug ?? null,
    });
    // DB side (fix P0 2026-07-06, audit dashboards) : ce beacon manquait —
    // RDV_INTENT n'avait JAMAIS une seule ligne en DB malgre une colonne
    // dediee dans /admin/analytics/medecins (col:"rdv") et une classification
    // CONVERSION_TYPES dans /admin/analytics/evenements. Le dashboard
    // affichait structurellement "—" sur ces colonnes, faisant croire a
    // "personne ne demande de RDV" alors que l'event existait cote GA4/
    // Plausible, juste invisible dans le panel admin.
    try {
      const payload = JSON.stringify({
        type: "RDV_INTENT",
        entityType: "Physician",
        entitySlug: physicianSlug,
        source: `sticky-cta:${mode}`,
        metadata: { tier: safeTier, trialBoosted: trialBoosted ?? null, city: citySlug ?? null },
      });
      if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
        navigator.sendBeacon("/api/track/event", new Blob([payload], { type: "application/json" }));
      }
    } catch {
      // analytics ne doit jamais bloquer la navigation
    }
  };

  const trackWhatsappClick = () => {
    // GA4/Plausible side
    track("physician:contact_whatsapp", { source: UTM_SOURCE });
    // DB side (fix P0 2026-07-06, audit dashboards) : meme defaut que
    // RDV_INTENT ci-dessus — WHATSAPP_CLICK jamais enregistre en DB malgre
    // une colonne dediee (col:"whatsapp") dans /admin/analytics/medecins.
    try {
      const payload = JSON.stringify({
        type: "WHATSAPP_CLICK",
        entityType: "Physician",
        entitySlug: physicianSlug,
        source: `sticky-cta:${UTM_SOURCE}`,
        metadata: { tier: safeTier, city: citySlug ?? null },
      });
      if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
        navigator.sendBeacon("/api/track/event", new Blob([payload], { type: "application/json" }));
      }
    } catch {
      // ignore
    }
  };

  // tel: direct (sans fenêtre UnclaimedCallButton) : un clic vaut intention
  // puis appel, cf. stickyCallTrackEvents.
  const trackPhoneDirect = () =>
    trackStickyDirectCall({ physicianSlug, tier: safeTier, trialBoosted, city: citySlug });

  return (
    <div
      aria-hidden={!visible}
      className={cn(
        // Positioned ABOVE the BottomNav (which is bottom-0 + ~62px tall on mobile).
        // bottom-[68px] = 64px nav + 4px gap.
        "fixed inset-x-0 bottom-[68px] z-40 lg:hidden",
        "border-t border-[var(--sahha-border-subtle)] bg-white/95 backdrop-blur-xl supports-[backdrop-filter]:bg-white/85",
        "pt-2 pb-2",
        "transition-transform duration-300 ease-[var(--ease-out)]",
        visible ? "translate-y-0" : "pointer-events-none translate-y-full",
      )}
      data-sticky-cta
      data-sticky-cta-mode={mode}
    >
      <div className="mx-auto flex max-w-xl items-stretch gap-2 px-3">
        {/* ── CTA primaire (cascade) ──────────────────────────────────────── */}
        {mode === "book" && (
          <a
            href={bookingAnchor}
            onClick={trackBookingStarted}
            data-utm-source={UTM_SOURCE}
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[var(--sahha-primary-600)] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_6px_16px_-4px_rgba(21,112,239,0.45)] active:scale-[0.98]"
          >
            <CalendarCheck size={18} weight="bold" />
            <span>{t("bookCta")}</span>
          </a>
        )}

        {mode === "call" && phone && (
          safeTier === "unclaimed" || !onlineBookingEnabled ? (
            <UnclaimedCallButton
              phone={phone}
              source={UTM_SOURCE}
              physicianSlug={physicianSlug}
              tier={safeTier}
              trialBoosted={trialBoosted}
              city={citySlug}
              ariaLabel={t("callAria", { name: physicianName })}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[var(--sahha-primary-600)] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_6px_16px_-4px_rgba(21,112,239,0.45)] active:scale-[0.98]"
            >
              <Phone size={18} weight="fill" />
              <span>{t("callCta")}</span>
            </UnclaimedCallButton>
          ) : (
            <a
              href={telHrefMa(phone)}
              onClick={trackPhoneDirect}
              data-utm-source={UTM_SOURCE}
              aria-label={t("callAria", { name: physicianName })}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[var(--sahha-primary-600)] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_6px_16px_-4px_rgba(21,112,239,0.45)] active:scale-[0.98]"
            >
              <Phone size={18} weight="fill" />
              <span>{t("callCta")}</span>
            </a>
          )
        )}

        {mode === "request_rdv" && physicianSlug && (
          <Link
            href={`/rdv/${physicianSlug}` as never}
            onClick={trackBookingStarted}
            data-utm-source={UTM_SOURCE}
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[var(--sahha-primary-600)] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_6px_16px_-4px_rgba(21,112,239,0.45)] active:scale-[0.98]"
          >
            <CalendarCheck size={18} weight="bold" />
            <span>{t("requestRdvCta")}</span>
          </Link>
        )}

        {/* Fiche non cliente sans numéro : /rdv/{slug} liste des praticiens
            de même spécialité joignables par téléphone. Pas de RDV_INTENT :
            ce n'est pas une demande adressée à ce praticien. */}
        {mode === "reachable" && physicianSlug && (
          <Link
            href={`/rdv/${physicianSlug}` as never}
            data-utm-source={UTM_SOURCE}
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[var(--sahha-primary-600)] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_6px_16px_-4px_rgba(21,112,239,0.45)] active:scale-[0.98]"
          >
            <MagnifyingGlass size={18} weight="bold" />
            <span>{t("similarCta")}</span>
          </Link>
        )}

        {mode === "similar" && (
          <Link
            href={similarHref as never}
            data-utm-source={UTM_SOURCE}
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[var(--sahha-primary-600)] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_6px_16px_-4px_rgba(21,112,239,0.45)] active:scale-[0.98]"
          >
            <MagnifyingGlass size={18} weight="bold" />
            <span>{t("similarCta")}</span>
          </Link>
        )}

        {/* ── CTA secondaires : WhatsApp + Phone (icon-only) ──────────────── */}
        {/* On affiche le téléphone icon-only en SECONDAIRE seulement si le mode
            primaire n'est PAS déjà "call" (sinon double bouton phone). */}
        {waLink ? (
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("whatsappAria", { name: physicianName })}
            onClick={trackWhatsappClick}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_4px_12px_-3px_rgba(37,211,102,0.5)] active:scale-95"
          >
            <WhatsappLogo size={20} weight="fill" />
          </a>
        ) : null}
        {phone && mode !== "call" ? (
          !isOnlineBookingActive({ tier: safeTier, onlineBookingEnabled }) ? (
            <UnclaimedCallButton
              phone={phone}
              source={UTM_SOURCE}
              physicianSlug={physicianSlug}
              tier={safeTier}
              trialBoosted={trialBoosted}
              city={citySlug}
              ariaLabel={t("callAria", { name: physicianName })}
              iconOnly
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[var(--sahha-border-default)] bg-white text-[var(--sahha-primary-700)] active:scale-95"
            />
          ) : (
            <a
              href={telHrefMa(phone)}
              aria-label={t("callAria", { name: physicianName })}
              onClick={trackPhoneDirect}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[var(--sahha-border-default)] bg-white text-[var(--sahha-primary-700)] active:scale-95"
            >
              <Phone size={18} weight="fill" />
            </a>
          )
        ) : null}
      </div>
    </div>
  );
}
